import { useMemo } from "react";
import { useACLContext } from "../context";
import type { PermissionRule, RoleName } from "@shield-acl/core";

/**
 * Estrutura detalhada de permissões do usuário
 */
export interface UserPermissionsInfo<TContext = any> {
  /** Todas as permissões efetivas (incluindo herdadas) */
  all: PermissionRule<TContext>[];

  /** Permissões diretas do usuário (não herdadas) */
  direct: PermissionRule<TContext>[];

  /** Permissões herdadas das roles */
  inherited: PermissionRule<TContext>[];

  /** Permissões agrupadas por role */
  byRole: Record<RoleName, PermissionRule<TContext>[]>;

  /** Permissões de negação (deny) */
  denials: PermissionRule<TContext>[];

  /** Permissões de permissão (allow) */
  allowances: PermissionRule<TContext>[];

  /** Lista única de ações disponíveis */
  actions: string[];

  /** Lista única de recursos disponíveis */
  resources: string[];

  /** Contagem total de permissões */
  count: number;
}

/**
 * Hook que lista todas as permissões efetivas do usuário atual
 * Fornece uma visão detalhada e organizada das permissões
 *
 * @returns Objeto com informações detalhadas sobre as permissões
 *
 * @example
 * ```tsx
 * const permissions = usePermissions()
 *
 * // Listar todas as permissões
 * console.log(`Total de permissões: ${permissions.count}`)
 * permissions.all.forEach(perm => {
 *   console.log(`- ${perm.action} on ${perm.resource}`)
 * })
 *
 * // Ver permissões por role
 * Object.entries(permissions.byRole).forEach(([role, perms]) => {
 *   console.log(`Role ${role}: ${perms.length} permissões`)
 * })
 *
 * // Verificar negações explícitas
 * if (permissions.denials.length > 0) {
 *   console.warn("Existem negações explícitas:")
 *   permissions.denials.forEach(denial => {
 *     console.warn(`- Negado: ${denial.action}`)
 *   })
 * }
 *
 * // Listar ações únicas disponíveis
 * console.log("Ações disponíveis:", permissions.actions)
 *
 * // Listar recursos únicos
 * console.log("Recursos acessíveis:", permissions.resources)
 * ```
 */
export function usePermissions<
  TContext = any,
>(): UserPermissionsInfo<TContext> {
  const { engine, user } = useACLContext<TContext>();

  return useMemo(() => {
    // Se não há usuário, retorna estrutura vazia
    if (!user) {
      return {
        all: [],
        direct: [],
        inherited: [],
        byRole: {},
        denials: [],
        allowances: [],
        actions: [],
        resources: [],
        count: 0,
      };
    }

    // Obtém todas as permissões do usuário
    const allPermissions = engine.getUserPermissions(user);

    // Separa permissões diretas
    const directPermissions = user.permissions || [];

    // Calcula permissões herdadas (todas menos as diretas)
    const inheritedPermissions = allPermissions.filter(
      (perm: PermissionRule<TContext>) => !directPermissions.includes(perm),
    );

    // Agrupa permissões por role
    const permissionsByRole: Record<RoleName, PermissionRule<TContext>[]> = {};

    // Para cada role do usuário, obtém suas permissões
    user.roles.forEach((roleName: RoleName) => {
      const role = engine.getRole(roleName);
      if (role) {
        permissionsByRole[roleName] = role.permissions;
      }
    });

    // Separa negações e permissões
    const denials = allPermissions.filter((perm: PermissionRule<TContext>) => perm.deny === true);
    const allowances = allPermissions.filter((perm: PermissionRule<TContext>) => perm.deny !== true);

    // Extrai ações únicas
    const uniqueActions = new Set<string>();
    allPermissions.forEach((perm: PermissionRule<TContext>) => {
      if (Array.isArray(perm.action)) {
        perm.action.forEach((action: string) => uniqueActions.add(action));
      } else {
        uniqueActions.add(perm.action);
      }
    });

    // Extrai recursos únicos (ignora undefined)
    const uniqueResources = new Set<string>();
    allPermissions.forEach((perm: PermissionRule<TContext>) => {
      if (perm.resource) {
        if (Array.isArray(perm.resource)) {
          perm.resource.forEach((resource: string) => uniqueResources.add(resource));
        } else {
          uniqueResources.add(perm.resource);
        }
      }
    });

    return {
      all: allPermissions,
      direct: directPermissions,
      inherited: inheritedPermissions,
      byRole: permissionsByRole,
      denials,
      allowances,
      actions: Array.from(uniqueActions).sort(),
      resources: Array.from(uniqueResources).sort(),
      count: allPermissions.length,
    };
  }, [engine, user]);
}

/**
 * Hook simplificado que retorna apenas o array de permissões
 *
 * @returns Array de todas as permissões efetivas
 *
 * @example
 * ```tsx
 * const permissions = usePermissionsList()
 *
 * permissions.forEach(perm => {
 *   console.log(`${perm.action} on ${perm.resource}`)
 * })
 * ```
 */
export function usePermissionsList<
  TContext = any,
>(): PermissionRule<TContext>[] {
  const { all } = usePermissions<TContext>();
  return all;
}

/**
 * Hook que retorna apenas as ações disponíveis ao usuário
 *
 * @returns Array de ações únicas
 *
 * @example
 * ```tsx
 * const actions = useAvailableActions()
 *
 * // ["posts.create", "posts.edit", "users.read", ...]
 * console.log("Ações disponíveis:", actions)
 * ```
 */
export function useAvailableActions(): string[] {
  const { actions } = usePermissions();
  return actions;
}

/**
 * Hook que retorna apenas os recursos acessíveis ao usuário
 *
 * @returns Array de recursos únicos
 *
 * @example
 * ```tsx
 * const resources = useAvailableResources()
 *
 * // ["posts", "users", "settings", ...]
 * console.log("Recursos acessíveis:", resources)
 * ```
 */
export function useAvailableResources(): string[] {
  const { resources } = usePermissions();
  return resources;
}
